/**
 * Módulos ativos do convênio, derivados dos produtos (`GET /product_staff/`).
 *
 * O produto Super App é a fonte de verdade das flags booleanas: as chaves abaixo
 * são exatamente as declaradas no `metadata` do default_product "Super App".
 * As flags do agreement (`useTheme().extraData.*Module`) não são mais consultadas.
 */
export interface AgreementModules {
  hasSuperApp: boolean;
  hasClub: boolean;
  hasBookstore: boolean;
  hasMagazine: boolean;
  /** Conveniência: existe algum produto de mídia (livro ou revista). */
  hasMedia: boolean;
  localModule: boolean;
  ecommerceModule: boolean;
  cpfModule: boolean;
  promocodeModule: boolean;
  customerCentralModule: boolean;
  pushNotificationModule: boolean;
  digitalMagazinesModule: boolean;
  econometerModule: boolean;
  subscriptionModule: boolean;
  bePartnerModule: boolean;
  exClientModule: boolean;
  moduleExternalCode: boolean;
  planSaleModulo: boolean;
  disableLibraryModule: boolean;
}

export const EMPTY_MODULES: AgreementModules = {
  hasSuperApp: false,
  hasClub: false,
  hasBookstore: false,
  hasMagazine: false,
  hasMedia: false,
  localModule: false,
  ecommerceModule: false,
  cpfModule: false,
  promocodeModule: false,
  customerCentralModule: false,
  pushNotificationModule: false,
  digitalMagazinesModule: false,
  econometerModule: false,
  subscriptionModule: false,
  bePartnerModule: false,
  exClientModule: false,
  moduleExternalCode: false,
  planSaleModulo: false,
  disableLibraryModule: false,
};

/**
 * O metadata da instância do produto guarda os valores como string (`{ value: "true" }`),
 * enquanto o do default_product guarda o schema. Lemos apenas a instância.
 */
function flag(product: Product | undefined, key: string): boolean {
  const value = product?.metadata?.[key]?.value;
  return value === true || value === 'true';
}

export function deriveModules(products: Product[] | undefined | null): AgreementModules {
  if (!products || products.length === 0) return EMPTY_MODULES;

  const active = products.filter((product) => product.active);
  // Em teoria só há um produto Super App ativo por convênio. Se a API alguma vez
  // devolver mais de um (ex.: durante uma migração), desempatamos pelo mais
  // recentemente atualizado em vez de depender da ordem da resposta — caso
  // contrário o conjunto de módulos inteiro mudaria conforme a ordenação do backend.
  const superApps = active.filter((product) => product.default_product?.is_superapp);
  const superApp = superApps.reduce<Product | undefined>((latest, product) => {
    if (!latest) return product;
    return product.updated_at > latest.updated_at ? product : latest;
  }, undefined);
  const bookstores = active.filter((product) => product.default_product?.is_bookstore);

  const localModule = flag(superApp, 'localModule');
  const ecommerceModule = flag(superApp, 'ecommerceModule');
  const hasMagazine = bookstores.some((product) => flag(product, 'hasMagazine'));
  const hasBookstore = bookstores.some((product) => !flag(product, 'hasMagazine'));

  return {
    hasSuperApp: !!superApp,
    // Regra do negócio: sem loja física nem online, não há clube.
    hasClub: !!superApp && (localModule || ecommerceModule),
    hasBookstore,
    hasMagazine,
    hasMedia: hasBookstore || hasMagazine,
    localModule,
    ecommerceModule,
    cpfModule: flag(superApp, 'cpfModule'),
    promocodeModule: flag(superApp, 'promocodeModule'),
    customerCentralModule: flag(superApp, 'customerCentralModule'),
    pushNotificationModule: flag(superApp, 'pushNotificationModule'),
    digitalMagazinesModule: flag(superApp, 'digitalMagazinesModule'),
    econometerModule: flag(superApp, 'econometerModule'),
    subscriptionModule: flag(superApp, 'subscriptionModule'),
    bePartnerModule: flag(superApp, 'bePartnerModule'),
    exClientModule: flag(superApp, 'exClientModule'),
    moduleExternalCode: flag(superApp, 'moduleExternalCode'),
    planSaleModulo: flag(superApp, 'planSaleModulo'),
    disableLibraryModule: flag(superApp, 'disableLibraryModule'),
  };
}
